import { useState, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { ComboboxFK } from './ComboboxFK';
import { useEntityQuickAdd } from './useEntityQuickAdd';
import { useDebounce } from '@/hooks/useDebounce';

export type SharedComboboxEntityType =
    | 'product'
    | 'customer'
    | 'vendor'
    | 'user'
    | 'material'
    | 'order'
    | 'quotation'
    | 'machine'
    | 'warehouse'
    | 'productionOrder'
    | 'dieCut'
    | 'layout'
    | 'printLayout'
    | 'technicalSpec'
    | 'plate'
    | 'process'
    | 'purchaseOrder'
    | 'receipt'
    | 'productType'
    | 'materialType';

interface SharedComboboxProps {
    type: SharedComboboxEntityType;
    value: string;
    valueLabel?: string;
    onChange: (id: string, opt?: any) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    hideLabel?: boolean;
    quickAddLabel?: string;
    canAdd?: boolean;
    onNameChange?: (name: string) => void;
    onCreated?: (id: string) => void;
    onQuickAddClick?: (name: string) => void;
    params?: Record<string, any>;
    className?: string;
    id?: string;
    name?: string;
    dataField?: string;
    addFormContent?: React.ReactNode;
    addFormTitle?: string;
    addModalSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    /** Breadcrumb hierarchy for nested modal context */
    breadcrumb?: string[];
    /** Show entity code badge (subLabel) inside selected trigger */
    showSelectedSubLabel?: boolean;
}

export function SharedCombobox(props: SharedComboboxProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 100);

    const stringifiedParams = JSON.stringify(props.params);
    const memoParams = useMemo(() => props.params, [stringifiedParams]);

    // Nút thêm mới theo quyền + form của danh mục cha (dùng chung với ComboboxFK trong các form)
    const quickAdd = useEntityQuickAdd(props.type, {
        params: props.params,
        breadcrumb: props.breadcrumb,
        canAdd: props.canAdd,
        quickAddLabel: props.quickAddLabel,
        addFormTitle: props.addFormTitle,
        addFormContent: props.addFormContent,
        onQuickAddClick: props.onQuickAddClick,
        onCreated: props.onCreated,
    });

    const { 
        data, 
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteQuery(
        trpc.combobox.getOptions.infiniteQueryOptions(
            {
                entityType: props.type as any,
                search: debouncedSearch,
                selectedValue: props.value,
                params: memoParams,
                limit: 50
            },
            {
                initialCursor: 0,
                getNextPageParam: (lastPage: any) => lastPage.nextCursor,
                // Luôn lấy dữ liệu mới khi mở form để thấy ngay bản ghi vừa tạo ở màn hình khác (không dùng cache 5 phút)
                staleTime: 0,
                refetchOnMount: 'always',
            }
        )
    );

    const stringifiedData = JSON.stringify(data);
    const options = useMemo(() => {
        const pages = data?.pages || [];
        const allItems = pages.flatMap((p: any) => p.items || []);
        
        return allItems.map((item: any) => {
            // Extract all string values to allow ComboboxFK to fuzzy match them locally
            const searchTags = Object.values(item.original || item)
                .filter(v => typeof v === 'string' || typeof v === 'number')
                .join(' ')
                .toLowerCase();

            const opt = {
                id: String(item.value),
                label: String(item.label),
                subLabel: item.subLabel || item.original?.productCode || item.original?.code || item.original?.materialId || item.original?.shortName,
                searchTags,
                original: item.original
            };

            return opt;
        });
    }, [stringifiedData]);

    return (
        <ComboboxFK
            {...props}
            showSelectedSubLabel={props.showSelectedSubLabel ?? props.type === 'product'}
            canAdd={quickAdd.canAdd}
            quickAddLabel={quickAdd.quickAddLabel}
            addFormContent={quickAdd.addFormContent}
            addFormTitle={quickAdd.addFormTitle}
            addModalSize={props.addModalSize || quickAdd.addModalSize}
            onCreated={quickAdd.onCreated}
            options={options}
            onSearchChange={setSearch}
            onChange={(id, selectedOption) => {
                const opt = selectedOption || options.find((o: any) => o.id === id);
                props.onChange(id, opt);
            }}
            loading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => {
                if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            }}
        />
    );
}

export default SharedCombobox;
